import { useCallback, useEffect, useRef, useState } from "react";
import {
  DistanceUnit,
  IWorkoutSetViewModel,
  WorkoutExerciseType,
} from "../models/workoutPlanViewModel";
import { splitDuration, trimDecimal } from "../utils/setValueFormat";

const FIELD_DEBOUNCE_MS = 500;
const KG_MIN = 0;
const KG_MAX = 9999;
const REPS_MIN = 0;
const REPS_MAX = 999;
/** Seconds: just under a day, the server's cap for Time and Time + Pace. */
const SECONDS_MAX = 86_399;
/** Metres: 999 km, the server's cap for Distance. */
const METERS_MAX = 999_999;
const RESISTANCE_MAX = 999;
const SPEED_MAX = 99;

/**
 * Values a set row hands back besides kg/reps. Only the ones the row shows are sent:
 * the band resistance on a band exercise, the speed on a Time + Pace one.
 */
export interface ISetExtraValues {
  resistanceKg?: number;
  speedKmh?: number;
}

export type SetUpdateHandler = (
  setId: number,
  kg: number | null,
  reps: number | null,
  extra?: ISetExtraValues,
) => Promise<void> | void;

interface IUseSetRowArgs {
  set: IWorkoutSetViewModel;
  disabled: boolean;
  onUpdate: SetUpdateHandler;
  exerciseType?: WorkoutExerciseType;
  distanceUnit?: DistanceUnit;
  isResistanceBand?: boolean;
}

/**
 * What the inputs hold, as typed. `reps` is the count, the metres or the kilometres
 * (Distance, by unit); a time is split over `minutes` + `seconds`.
 */
interface ISetInputs {
  kg: string;
  reps: string;
  minutes: string;
  seconds: string;
  resistance: string;
  speed: string;
}

const formatPositive = (value: number | null | undefined): string =>
  value && value > 0 ? String(value) : "";

const isTimed = (type: WorkoutExerciseType) =>
  type === WorkoutExerciseType.Time || type === WorkoutExerciseType.TimePace;

const toInputs = (
  set: IWorkoutSetViewModel,
  type: WorkoutExerciseType,
  unit: DistanceUnit,
): ISetInputs => {
  const { minutes, seconds } = splitDuration(set.reps);
  const timed = isTimed(type) && set.reps > 0;
  const reps =
    type === WorkoutExerciseType.Distance && unit === DistanceUnit.Kilometers && set.reps > 0
      ? trimDecimal(set.reps / 1000, 3)
      : formatPositive(set.reps);
  return {
    kg: formatPositive(set.kg),
    reps,
    minutes: timed && minutes > 0 ? String(minutes) : "",
    seconds: timed ? String(seconds).padStart(minutes > 0 ? 2 : 1, "0") : "",
    resistance: formatPositive(set.resistanceKg),
    speed: formatPositive(set.speedKmh),
  };
};

const parseDecimal = (raw: string): number | null =>
  raw === "" ? null : Number(raw.replace(",", "."));

const clamp = (value: number, max: number, min = 0): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(max, Math.max(min, value));
};

/** The reps column (count, seconds or metres) the typed inputs stand for; null when empty. */
const parseReps = (inputs: ISetInputs, type: WorkoutExerciseType, unit: DistanceUnit): number | null => {
  if (isTimed(type)) {
    if (inputs.minutes === "" && inputs.seconds === "") return null;
    const minutes = inputs.minutes === "" ? 0 : parseInt(inputs.minutes, 10);
    const seconds = inputs.seconds === "" ? 0 : parseInt(inputs.seconds, 10);
    return clamp(Math.trunc(minutes * 60 + seconds), SECONDS_MAX);
  }
  if (inputs.reps === "") return null;
  if (type === WorkoutExerciseType.Distance) {
    const meters =
      unit === DistanceUnit.Kilometers
        ? Math.round((parseDecimal(inputs.reps) ?? 0) * 1000)
        : parseInt(inputs.reps, 10);
    return clamp(meters, METERS_MAX);
  }
  return clamp(Math.trunc(parseInt(inputs.reps, 10)), REPS_MAX, REPS_MIN);
};

const keepDecimal = (raw: string, maxLength: number) => raw.replace(/[^\d.,]/g, "").slice(0, maxLength);
const keepDigits = (raw: string, maxLength: number) => raw.replace(/\D/g, "").slice(0, maxLength);

/**
 * Owns the locally-typed values for one set row and flushes a debounced
 * PATCH 500ms after the user stops typing. The row's measurement type decides which
 * inputs there are and how they become the stored kg / reps / resistance / speed.
 *
 * Resync rules:
 *   • When the row identity changes (`set.id`) — or the exercise's type or distance unit,
 *     which change what the inputs mean — reset to the row's server values, unless a
 *     debounced edit is still waiting to flush: then the typed value wins and flushes
 *     against the old id, which the dialog hook maps to the new one. The resync is not
 *     lost, only postponed: it runs when the flushed values come back as props.
 *   • Otherwise, the local typed value wins — the parent re-rendering with stale server
 *     props (e.g. the previous PATCH hasn't completed yet) must NOT clobber the user's
 *     in-flight input. Once typing settles and the debounce flushes, the server agrees
 *     with whatever the user typed.
 */
export const useSetRow = ({
  set,
  disabled,
  onUpdate,
  exerciseType = WorkoutExerciseType.KgReps,
  distanceUnit = DistanceUnit.Meters,
  isResistanceBand = false,
}: IUseSetRowArgs) => {
  const [inputs, setInputs] = useState<ISetInputs>(() => toInputs(set, exerciseType, distanceUnit));
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFlushRef = useRef<(() => void) | null>(null);
  const identity = `${set.id}|${exerciseType}|${distanceUnit}`;
  const previousIdentityRef = useRef(identity);

  useEffect(() => {
    if (previousIdentityRef.current === identity) return;
    // A debounced edit is still waiting to flush: the typed value wins for now,
    // so leave the ref on the old identity — the resync is retried (against the
    // values the flush lands) instead of being skipped for good, which would strand
    // the input showing something that was never persisted.
    if (debounceRef.current) return;
    previousIdentityRef.current = identity;
    setInputs(toInputs(set, exerciseType, distanceUnit));
    // `set` is read for its values; the dependencies below are the ones that change them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, set.kg, set.reps, set.resistanceKg, set.speedKmh]);

  const flush = useCallback(
    (next: ISetInputs) => {
      const parsedKg = parseDecimal(next.kg);
      const safeKg = parsedKg === null ? null : clamp(parsedKg, KG_MAX, KG_MIN);
      const safeReps = parseReps(next, exerciseType, distanceUnit);

      // An emptied resistance / speed field means none (0), not "leave as is".
      const extra: ISetExtraValues = {};
      if (isResistanceBand) {
        extra.resistanceKg = clamp(parseDecimal(next.resistance) ?? 0, RESISTANCE_MAX);
      }
      if (exerciseType === WorkoutExerciseType.TimePace) {
        extra.speedKmh = clamp(parseDecimal(next.speed) ?? 0, SPEED_MAX);
      }

      const extraChanged =
        (extra.resistanceKg !== undefined && extra.resistanceKg !== (set.resistanceKg ?? 0)) ||
        (extra.speedKmh !== undefined && extra.speedKmh !== (set.speedKmh ?? 0));
      if (safeKg === set.kg && safeReps === set.reps && !extraChanged) return;

      if (Object.keys(extra).length > 0) {
        void onUpdate(set.id, safeKg, safeReps, extra);
      } else {
        void onUpdate(set.id, safeKg, safeReps);
      }
    },
    [onUpdate, exerciseType, distanceUnit, isResistanceBand, set.id, set.kg, set.reps, set.resistanceKg, set.speedKmh],
  );

  const scheduleFlush = useCallback(
    (next: ISetInputs) => {
      if (disabled) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const fire = () => {
        debounceRef.current = null;
        pendingFlushRef.current = null;
        flush(next);
      };
      pendingFlushRef.current = fire;
      debounceRef.current = setTimeout(fire, FIELD_DEBOUNCE_MS);
    },
    [disabled, flush],
  );

  const change = useCallback(
    (field: keyof ISetInputs, value: string) => {
      const next = { ...inputs, [field]: value };
      setInputs(next);
      scheduleFlush(next);
    },
    [inputs, scheduleFlush],
  );

  const handleKgChange = useCallback((raw: string) => change("kg", keepDecimal(raw, 7)), [change]);

  const handleRepsChange = useCallback(
    (raw: string) => {
      if (exerciseType === WorkoutExerciseType.Distance) {
        change(
          "reps",
          distanceUnit === DistanceUnit.Kilometers ? keepDecimal(raw, 7) : keepDigits(raw, 6),
        );
        return;
      }
      change("reps", keepDigits(raw, 3));
    },
    [change, exerciseType, distanceUnit],
  );

  const handleMinutesChange = useCallback((raw: string) => change("minutes", keepDigits(raw, 4)), [change]);
  const handleSecondsChange = useCallback((raw: string) => change("seconds", keepDigits(raw, 2)), [change]);
  const handleResistanceChange = useCallback((raw: string) => change("resistance", keepDecimal(raw, 6)), [change]);
  const handleSpeedChange = useCallback((raw: string) => change("speed", keepDecimal(raw, 5)), [change]);

  // Force any in-flight debounce to fire NOW. Hooked to input blur so that
  // clicking "Add Set" (or any other UI element) commits the user's typed
  // value to the cache before the next click handler reads from it —
  // otherwise the new set inherits the stale pre-debounce value.
  const flushPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const fire = pendingFlushRef.current;
    pendingFlushRef.current = null;
    fire?.();
  }, []);

  // On unmount, flush any pending change instead of dropping it — so closing
  // the dialog mid-debounce still saves the user's last edit.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      pendingFlushRef.current?.();
    };
  }, []);

  return {
    kgInput: inputs.kg,
    repsInput: inputs.reps,
    minutesInput: inputs.minutes,
    secondsInput: inputs.seconds,
    resistanceInput: inputs.resistance,
    speedInput: inputs.speed,
    handleKgChange,
    handleRepsChange,
    handleMinutesChange,
    handleSecondsChange,
    handleResistanceChange,
    handleSpeedChange,
    flushPending,
  };
};
